// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.FilterPropertiesSchema.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { asReference, toJS } from 'mobx';
import SCHEMA_DEF from 'logic/data/Izenda.FilterPropertiesSchema';
import { DATA_TYPES } from 'logic/data/Izenda.ReportDesignerConstants';
import DateTimeUtils from 'share/utils/Izenda.DateTimeUtils';
import commonDataStore from 'logic/Izenda.ReportDesignerCommonDataStore';
import CONSTANTS from 'logic/data/Izenda.ReportDesignerFormatsConstants';
import localizer from 'share/localization/Izenda.Localizer';
import QuerySourceDropDown from 'modules/ReportDesigner/Fields/Filter/Izenda.QuerySourceDropDown';
import Panel from 'share/components/controls/Izenda.Panel';
import OperatorGroup from 'modules/ReportDesigner/Fields/Filter/Izenda.OperatorGroup';
import FontStyle from 'share/components/controls/Izenda.FontStyle';
import FontFormat from 'share/components/controls/Izenda.FontFormat';
import ButtonHighlight from 'share/components/controls/Izenda.ButtonHighlight';
import ColorStyling from 'share/components/controls/Izenda.ColorStyling';
import { find, forEach, concat, get } from 'lodash/fp';
import typeof ReportDesigner from 'logic/models/Izenda.ReportDesigner';
import typeof ReportDesignerFieldsStore from 'logic/Izenda.ReportDesignerFieldsStore';
import typeof ReportDesignerFilter from 'logic/models/Izenda.ReportDesignerFilter';
import typeof ValidationManager from 'share/validation/Izenda.ValidationManager';

const WIDGETS = {
  QuerySourceDropDown,
  Panel,
  OperatorGroup,
  FontStyle,
  FontFormat,
  ButtonHighlight,
  ColorStyling
};

export default function getFilterSchema(
  editorComp: any,
  reportDesignerModel: ReportDesigner,
  fieldStore: ReportDesignerFieldsStore,
  reportFilter: ReportDesignerFilter,
  valManager: ValidationManager
) {
  ensure(!!reportDesignerModel, 'reportDesignerModel must be set');

  const configField = reportFilter.configField || {};

  const validateFilterAlias = (newName: string) => {
    const isExisting =
      reportFilter.checkExistingFilter &&
      reportFilter.checkExistingFilter(newName, reportFilter.configField.fieldDetails);

    const formatGroup = find(item => item.value === CONSTANTS.FORMAT_GROUP_CONFIG.FILTER, CONSTANTS.FORMAT_GROUP) || {};
    const formatGroupName = formatGroup.label;
    const message = String.format(CONSTANTS.MESSAGES.M01, formatGroupName);

    return Promise.resolve(isExisting ? message : '');
  };

  return {
    // define data to render UI
    definedSchema: SCHEMA_DEF,

    // list of widgets which are used to render UI
    widgets: WIDGETS,

    // data to binding to fields
    source: configField,

    // properties for different fields
    mappingProps: {
      validateItemValue: () => {
        valManager.validate().then(success => {
          success && valManager.reset();
          return success;
        });
      },

      configField,

      fieldDetails: configField.fieldDetails,

      updateSourceValue: fieldStore.getOperatorValueSource && fieldStore.getOperatorValueSource.bind(fieldStore),

      filterAliasInputType:
        configField.fieldDetails.sourceFieldFilterable && configField.fieldDetails.sourceFieldVisible
          ? 'text'
          : 'password',

      isFilterAliasDisabled:
        configField.fieldDetails.sourceFieldFilterable === false ||
        configField.fieldDetails.sourceFieldVisible === false,

      isParameter: configField.fieldDetails.isParameter,

      fieldStore,

      reportFilters: fieldStore.reportFilters,

      isDisableCascading: configField.fieldDetails.isDisableCascading,

      getSortTypeValue: () => configField.fieldDetails
    },

    // defined-function which fields need to execute to get its data
    mappingSource: {
      getAliasValidation: () => ({
        presence: {
          presence: true,
          message: localizer.getText('FILTER_PROPERTIES_MESSAGE_FILTER_ALIAS_VALUE_REQUIRED')
        },
        action: asReference(() => validateFilterAlias)
      }),

      getQuerySource: () => {
        const querySource = reportFilter.commonData && reportFilter.commonData.querySource;
        querySource &&
          forEach(
            item =>
              (item.groupByValue = String.format(
                '{0}.{1}.{2}',
                item.connectionName,
                item.querySourceCategoryName,
                item.sourceDataObjectName
              )),
            querySource
          );

        // Add aggregated Fields
        const reportBody = fieldStore.reportBody || {};
        const aggregatedFields = reportBody.aggregatedFields || [];
        return concat(toJS(querySource), aggregatedFields);
      },

      getQuerySourceValue: () => configField.fieldDetails.getFilterId && configField.fieldDetails.getFilterId(),
      getDataFormat: () => {
        const fieldDetails = configField.fieldDetails || {};
        return fieldStore.loadDataType(fieldDetails.dataType);
      },
      getFontStyle: () => ({
        fontBold: !!configField.getValueByKey('fontBold'),
        fontItalic: !!configField.getValueByKey('fontItalic'),
        fontUnderline: !!configField.getValueByKey('fontUnderline')
      }),
      getFontFormat: () => {
        return {
          fontFamily: configField.getValueByKey('fontFamily'),
          fontSize: configField.getValueByKey('fontSize')
        };
      },
      getColorStyling: () => {
        return {
          textColor: configField.getValueByKey('textColor'),
          backgroundColor: configField.getValueByKey('backgroundColor')
        };
      },

      getFilterAlias: () => configField.getValueByKey('alias'),
      getDataFormats: () => {
        const fieldDetails = configField.fieldDetails || {};
        const dataType = fieldDetails.dataType;

        return commonDataStore.loadFilterDataFormat(dataType).then(formats =>
          // filter date time format that current user setting can supported
          dataType !== DATA_TYPES.DATE_TIME ? formats : DateTimeUtils.filterDateTimeFormat(formats)
        );
      },

      isAggregatedField: () => !!configField.fieldDetails.reportPartTitle,
      isPhysicalField: () => !configField.fieldDetails.reportPartTitle,
      isFilterFormatingHidden: () => !reportFilter.visible,
      isHiddenUseLookup: () =>
        !get('filterInfo.operator.allowLookup', configField) || !configField.fieldDetails.showUseLookupCheckbox
    },

    // mapping data from editor to report-designer- model
    onChangeValue: (schemaData: any, key: string, changedKeyPath: Array<string>, changedValue: any) => {
      const groupData = changedKeyPath && changedKeyPath.length ? get(changedKeyPath, schemaData) : schemaData;
      const fieldData = groupData[key];

      fieldData && (fieldData.value = changedValue);

      const sourceInfo = schemaData.sourceInfo || {};
      const filterSettings = schemaData.filterSettings || {};
      const filterFormating = schemaData.filterFormating || {};
      const filterOperator = schemaData.filterOperator || {};

      const fontFormat = filterFormating.fontFormat.value || {};
      const fontStyle = filterFormating.fontStyle.value || {};
      const color = filterFormating.color.value || {};

      configField.fieldDetails.setFilterDetails &&
        configField.fieldDetails.setFilterDetails({
          // Source Info value
          sourceDataObjectName: sourceInfo.sourceDataObjectName.value,
          querySourceType: sourceInfo.querySourceType.value,
          querySourceFieldId: configField.fieldDetails.querySourceFieldId,

          // Filter lookup setting
          filterLookupType: configField.fieldDetails.filterLookupType,
          filterLookupStatus: configField.fieldDetails.filterLookupStatus,
          useLookup: filterOperator.useLookup.value,

          // Filter Settings Details
          alias: filterSettings.alias.value,
          visible: filterSettings.visible.value,
          required: filterSettings.required.value,
          cascading: filterSettings.cascading.value,
          sortType: filterSettings.sortType.value,

          // Filter Formating Details
          dataFormatId: filterFormating.dataFormatId.value,

          fontFamily: fontFormat.fontFamily,
          fontSize: fontFormat.fontSize,

          fontBold: fontStyle.fontBold,
          fontItalic: fontStyle.fontItalic,
          fontUnderline: fontStyle.fontUnderline,

          textColor: color.textColor,
          backgroundColor: color.backgroundColor
        });

      // update source filter when change sort type settings
      if (key === 'sortType') {
        fieldStore.getOperatorValueSource(configField);
      }

      // Update disable cascading and reset filter source when use lookup of a field is change
      if (key === 'useLookup') {
        reportFilter.disableCascading();
        configField.resetOperatorSourceDisplay();
      }
    }
  };
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/Filter/Izenda.FilterPropertiesSchema.js